import { z } from 'zod'
import {
  TRAINER_CHANNEL_READ_CONTRACT_VERSION,
  trainerChannelAttachmentSchema,
  trainerChannelMessageSchema,
  trainerChannelMessageWindowSchema,
  trainerChannelReadSideEffectsSchema,
  trainerChannelResponseSchema,
  trainerChannelSummarySchema,
} from './trainer-channel-read'

export const TRAINER_CLIENT_INVITE_READ_CONTRACT_VERSION = TRAINER_CHANNEL_READ_CONTRACT_VERSION

const nullableString = z.string().nullable()
const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const trainerClientInviteReadQuerySchema = z.object({
  orgId: optionalQueryString,
  includeArchivedAttachments: optionalQueryString,
  beforeMessageId: optionalQueryString,
  afterMessageId: optionalQueryString,
}).passthrough()

export const trainerClientInviteSummarySchema = z.object({
  clientPath: z.string().min(1),
  expiresAt: z.string().min(1),
  revokedAt: nullableString,
  accessCode: nullableString.optional(),
  codeEntryPath: z.literal('/chat').optional(),
}).passthrough()

export const trainerClientInviteAttachmentPolicySchema = z.object({
  retentionMode: z.string().min(1),
  hardDeleteAvailable: z.boolean(),
  providerDeleteCalled: z.boolean(),
  scanningMode: z.string().min(1),
  signedUrlTtlSeconds: z.object({
    min: z.number().finite(),
    default: z.number().finite(),
    max: z.number().finite(),
  }).passthrough(),
}).passthrough()

export const trainerClientChannelEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  inviteStore: z.literal('KV'),
  tablesRead: z.tuple([
    z.literal('trainer_channels'),
    z.literal('trainer_channel_messages'),
    z.literal('trainer_channel_attachments'),
  ]),
  projection: z.literal('trainer-client-channel-messages-with-attachments'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerClientInviteCodeLookupResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CLIENT_INVITE_READ_CONTRACT_VERSION),
  invite: trainerClientInviteSummarySchema,
  sideEffects: trainerChannelReadSideEffectsSchema,
}).passthrough()

export const trainerClientChannelResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CLIENT_INVITE_READ_CONTRACT_VERSION),
  invite: trainerClientInviteSummarySchema,
  channel: trainerChannelSummarySchema,
  messages: z.array(trainerChannelMessageSchema),
  messageWindow: trainerChannelMessageWindowSchema,
  attachmentPolicy: trainerClientInviteAttachmentPolicySchema,
  channelEvidence: trainerClientChannelEvidenceSchema,
  sideEffects: trainerChannelReadSideEffectsSchema,
}).passthrough()

export const trainerEventChannelResponseSchema = trainerChannelResponseSchema

export {
  trainerChannelAttachmentSchema,
  trainerChannelMessageSchema,
  trainerChannelMessageWindowSchema,
  trainerChannelReadSideEffectsSchema,
  trainerChannelSummarySchema,
}

export type TrainerClientInviteReadQuery = z.infer<typeof trainerClientInviteReadQuerySchema>
export type TrainerClientInviteSummary = z.infer<typeof trainerClientInviteSummarySchema>
export type TrainerClientInviteAttachmentPolicy = z.infer<typeof trainerClientInviteAttachmentPolicySchema>
export type TrainerClientChannelEvidence = z.infer<typeof trainerClientChannelEvidenceSchema>
export type TrainerClientInviteCodeLookupResponse = z.infer<typeof trainerClientInviteCodeLookupResponseSchema>
export type TrainerClientChannelResponse = z.infer<typeof trainerClientChannelResponseSchema>
export type TrainerEventChannelResponse = z.infer<typeof trainerEventChannelResponseSchema>
